import React, { useState, useEffect } from "react";
import { Settings, Save, Eye, EyeOff, CheckCircle2, Info, ExternalLink } from "lucide-react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { PageHeader, AppButton } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import "../Settings/settings.scss";

const ZOHO_DATACENTERS = [
  { value: "https://accounts.zoho.in", label: "India (.in)" },
  { value: "https://accounts.zoho.com", label: "US (.com)" },
  { value: "https://accounts.zoho.eu", label: "EU (.eu)" },
  { value: "https://accounts.zoho.com.au", label: "Australia (.com.au)" },
  { value: "https://accounts.zoho.jp", label: "Japan (.jp)" },
];

const ZohoConfigPage = () => {
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [form, setForm] = useState({
    enabled: false,
    clientId: "",
    clientSecret: "",
    redirectUri: "https://api.lmscampus.com/api/zoho/callback",
    defaultDatacenter: "https://accounts.zoho.in",
    hasClientSecret: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/sa/zoho");
        setForm({
          enabled: data.enabled || false,
          clientId: data.clientId || "",
          clientSecret: data.clientSecret || "",
          redirectUri: data.redirectUri || "https://api.lmscampus.com/api/zoho/callback",
          defaultDatacenter: data.defaultDatacenter || "https://accounts.zoho.in",
          hasClientSecret: data.hasClientSecret || false,
        });
      } catch { /* defaults */ }
      setLoading(false);
    })();
  }, []);

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const toggleSecret = (field) => setShowSecrets((p) => ({ ...p, [field]: !p[field] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/sa/zoho", {
        enabled: form.enabled,
        clientId: form.clientId,
        clientSecret: form.clientSecret,
        redirectUri: form.redirectUri,
        defaultDatacenter: form.defaultDatacenter,
      });
      showToast("Zoho CRM settings saved!");
      const { data } = await apiClient.get("/sa/zoho");
      setForm({
        enabled: data.enabled || false,
        clientId: data.clientId || "",
        clientSecret: data.clientSecret || "",
        redirectUri: data.redirectUri || "https://api.lmscampus.com/api/zoho/callback",
        defaultDatacenter: data.defaultDatacenter || "https://accounts.zoho.in",
        hasClientSecret: data.hasClientSecret || false,
      });
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save.", "error");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="d-flex justify-content-center p-4"><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <div className="sa-settings">
      <PageHeader
        title="Zoho CRM Configuration"
        description="Configure Zoho OAuth credentials for institute CRM integrations"
        icon={Settings}
      />

      <div className="sa-card">
        <div className="sa-card-head">
          <Settings size={16} className="me-2" /> Zoho OAuth App Credentials
          <AppButton variant="primary" size="sm" onClick={handleSave} disabled={saving} style={{ marginLeft: "auto" }}>
            <Save size={14} className="me-1" /> {saving ? "Saving…" : "Save"}
          </AppButton>
        </div>
        <div className="sa-card-body">
          <div style={{ fontSize: "0.78rem", color: "var(--text-tertiary, #9ca3af)", lineHeight: 1.5, marginBottom: "1rem" }}>
            <Info size={13} className="me-1" style={{ position: "relative", top: -1 }} />
            These credentials are used for all institute Zoho CRM connections. Create a Server-based Application in your{" "}
            <a href="https://api-console.zoho.in/" target="_blank" rel="noopener noreferrer">
              Zoho API Console <ExternalLink size={11} />
            </a>.
          </div>

          <Row className="g-3">
            <Col md={12}>
              <Form.Check
                type="switch"
                id="zoho-enabled"
                label="Enable Zoho CRM Integration"
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Client ID</Form.Label>
                <Form.Control size="sm" value={form.clientId} onChange={update("clientId")} placeholder="e.g. 1000.XXXXXXXXXX.XXXXXXXXXX" />
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>From Zoho API Console → Client Secret tab</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">
                  Client Secret
                  {form.hasClientSecret && <CheckCircle2 size={13} className="ms-1 text-success" />}
                </Form.Label>
                <div className="d-flex gap-1">
                  <Form.Control
                    size="sm"
                    type={showSecrets.clientSecret ? "text" : "password"}
                    value={form.clientSecret}
                    onChange={update("clientSecret")}
                    placeholder="Enter client secret…"
                  />
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggleSecret("clientSecret")} style={{ padding: "0.25rem 0.5rem" }}>
                    {showSecrets.clientSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>From Zoho API Console → Client Secret tab</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Redirect URI</Form.Label>
                <Form.Control size="sm" value={form.redirectUri} onChange={update("redirectUri")} placeholder="https://api.lmscampus.com/api/zoho/callback" />
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>Must match the redirect URI configured in Zoho API Console</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="sa-settings__label">Default Data Center</Form.Label>
                <Form.Select size="sm" value={form.defaultDatacenter} onChange={update("defaultDatacenter")}>
                  {ZOHO_DATACENTERS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>Institutes can override this when connecting</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default ZohoConfigPage;
